import {
    Workflow,
    WorkflowAction,
    WorkflowAction_Limits,
    WorkflowStep,
    WorkflowStep_PullRequest,
    WorkflowTrigger,
    WorkflowTrigger_Manual,
    WorkflowTriggerContext,
    WorkflowTriggerContext_Projects,
} from "gitpod-next-api/gitpod/v1/workflow_pb";
import type { CentralIconBaseProps } from "central-icons/CentralIconBase";
import IconFocusAuto from "central-icons/IconFocusAuto";
import IconShieldCheck3 from "central-icons/IconShieldCheck3";
import IconFileText from "central-icons/IconFileText";
import { DEFAULT_WORKFLOW_LIMITS } from "@/utils/automations";
import IconFileBend from "central-icons/IconFileBend";

const AUTOMATION_TEMPLATES_INTERNAL = {
    "start-from-scratch": {
        id: "start-from-scratch",
        title: "Start from scratch",
        description: "Create a custom automation workflow from scratch.",
        icon: IconFileBend,
        iconColor: "text-content-brand",
        iconBgColor: "bg-surface-brand-subtle",
        enabled: true,
        trigger: new WorkflowTrigger({
            trigger: {
                case: "manual",
                value: new WorkflowTrigger_Manual(),
            },
            context: new WorkflowTriggerContext({
                context: {
                    case: "projects",
                    value: new WorkflowTriggerContext_Projects({
                        projectIds: [],
                    }),
                },
            }),
        }),
        steps: [
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: "With prompts you can send messages to Ona Agent",
                    },
                },
            }),
            new WorkflowStep({
                step: {
                    case: "task",
                    value: {
                        command: "echo 'with commands you can run shell commands'",
                    },
                },
            }),
        ],
    },
    "automated-dev-environment-setup": {
        id: "automated-dev-environment-setup",
        title: "Automated dev environment setup",
        description:
            "Ensures each repository has a consistent dev environment by analyzing the codebase, updating devcontainer and editor configuration, and opening a pull request with the changes.",
        enabled: true,
        icon: IconFocusAuto,
        iconColor: "text-content-brand-accent-05",
        iconBgColor: "bg-surface-brand-accent-07",
        trigger: new WorkflowTrigger({
            trigger: {
                case: "manual",
                        value: new WorkflowTrigger_Manual(),
                    },
                    context: new WorkflowTriggerContext({
                        context: {
                            case: "projects",
                            value: new WorkflowTriggerContext_Projects({
                                projectIds: [],
                            }),
                        },
                    }),
                }),
                steps: [
                    new WorkflowStep({
                        step: {
                            case: "agent",
                            value: {
                                prompt: `Create a high-quality, fully working “development environment as code” configuration for the current environment.

The setup must work for:
	•	Ona (Gitpod Flex) development environments, which use DevContainer configurations.
Do not confuse this with Gitpod Classic (Gitpod Dedicated), which uses .gitpod.yml.
	•	All Git repositories mounted under the DevContainer workspace.

Terminology
	•	apps: Source code intended to be built and shipped.
	•	dev tool: Any tool used for compiling, building, publishing, testing, profiling, debugging, or accessing an app's infrastructure.

Required Process
	1.	Read the documentation (see Allowed Sources) to fully understand:
	•	Ona automations, secrets, environment variables, CLI, and prebuilds.
	•	DevContainer fundamentals and how they integrate with Ona and VS Code.
	2.	Analyze the source code and identify:
	•	Documentation on dev setup or contribution guidelines.
	•	Configuration files for containers, IDEs, build tools, environment variables, etc.
	3.	Update all necessary files, including:
	•	devcontainer.json
	•	Ona automations (tasks and services)
	•	Any other files required to meet the Success Criteria.
	4.	Run the Acceptance Tests for all code you have created and iterate until the last run of every Acceptance Test has been successful.
	5.	Do not create any documentation files.

Success Criteria
	•	The DevContainer includes all tools needed to work with any file in any repo in this environment.
	•	Ona automation services exist for every service required or recommended to run any app in this environment.
	•	Ona automation services exist for every app, for the standard or documented configurations.
	•	Ona automation tasks exist for all standard or documented development workflows for this repo.
	•	If an app exposes a TCP port, the DevContainer must forward it.
	•	If an app exposes an HTTP/HTTPS port, the corresponding Ona automation service must expose it by running "gitpod env port open" before starting the app.
	•	The DevContainer must install all VS Code extensions necessary to work effectively with the files and services in this environment.
	
Acceptance Tests:
	•	The command "gitpod auto update <filename>" succeeds
	•	The command "gitpod env devcontianer validate" succeeds
	•	The DevContainer rebuilds successfully.
	•	All installed tools launch successfully and are available in the correct version
	•	Ona automation tasks start and finish successfully. 	
	•	Ona automation services successfully reach the state "ready".
	•	Apps exposed via Ona port respond successfully when curl'ing the port. 	

Allowed Sources
	•	Ona documentation: automations, secrets, environment variables, CLI, prebuilds, DevContainers
https://ona.com/docs/llms.txt
	•	DevContainer documentation: https://containers.dev/
	•	DevContainers in VS Code: https://code.visualstudio.com/docs/devcontainers/containers
	•	DevContainer base images: https://hub.docker.com/r/microsoft/devcontainers
	•	VS Code extensions:
	•	https://marketplace.visualstudio.com/vscode
	•	https://open-vsx.org/
	•	Any publicly available DevContainer features
	•	Anything installable within a Dockerfile
                                `,
                            },
                        },
                    }),
                    new WorkflowStep({
                        step: {
                            case: "pullRequest",
                            value: new WorkflowStep_PullRequest({
                                title: "Ona: automated dev environment setup",
                                description:
                                "The PR has been automatically created by Ona Agent to set up a consistent development environment for this repository. Please review the changes and merge if everything looks good.",
                            branch: "ona/automated-dev-environment-setup",
                            draft: true,
                        }),
                    },
                }),
            ],
        },
    "cve-mitigation-and-version-updates": {
        id: "cve-mitigation-and-version-updates",
                title: "CVE mitigation and version updates",
                description:
                    "Analyzes a specific CVE, determines if the repository is affected, and if so, automatically remediates the vulnerability by updating dependencies, migrating code to new APIs, running tests, and creating a pull request with the complete fix.",
                enabled: true,
                icon: IconShieldCheck3,
                iconColor: "text-content-brand-accent-01",
                iconBgColor: "bg-surface-brand-accent-01",
                trigger: new WorkflowTrigger({
                    trigger: {
                        case: "manual",
                        value: new WorkflowTrigger_Manual(),
                    },
                    context: new WorkflowTriggerContext({
                        context: {
                            case: "projects",
                            value: new WorkflowTriggerContext_Projects({
                                projectIds: [],
                            }),
                        },
                    }),
                }),
                steps: [
                    // Step 1: Research CVE
                    new WorkflowStep({
                        step: {
                            case: "agent",
                            value: {
                                prompt: "Research {CVE_ID}: what is the vulnerability, which library/package, what versions are affected, what versions have the fix, and which specific functions or code patterns trigger it. Do not look at repository code yet. Output: one sentence status only.",
                            },
                        },
                    }),
                    // Step 2: Find dependency
                    new WorkflowStep({
                        step: {
                            case: "agent",
                            value: {
                                prompt: "Check if this repository has the vulnerable library. Look at dependency files and analyze the full dependency tree including transitive dependencies. Report the version and whether it's direct or transitive. If not found or already patched, say so and skip remaining work. Do not analyze source code yet. Output: one sentence status only.",
                            },
                        },
                    }),
                    // Step 3: Check reachability
                    new WorkflowStep({
                        step: {
                            case: "agent",
                            value: {
                                prompt: "If the vulnerable library was found: search source code to find if our code calls the vulnerable functions. Show the call chain. If the library was not found or not reachable, skip this work. Do not modify any files yet. Output: one sentence status only.",
                            },
                        },
                    }),
                    // Step 4: Apply fix
                    new WorkflowStep({
                        step: {
                            case: "agent",
                            value: {
                                prompt: "If the vulnerability is reachable: update the dependency to the fixed version, adjust code if APIs changed, and run tests. Otherwise skip this work. Do not create a PR yet. Output: one sentence status only.",
                            },
                        },
                    }),
                    // Step 5: Create PR
                    new WorkflowStep({
                        step: {
                            case: "pullRequest",
                            value: new WorkflowStep_PullRequest({
                                title: "fix: CVE remediation",
                                description: "Automated CVE remediation by Ona Agent.",
                                branch: "ona/cve-remediation",
                                draft: true,
                            }),
                        },
                    }),
                    // Step 6: Generate report
                    new WorkflowStep({
                        step: {
                        case: "agent",
                        value: {
                            prompt: "Generate a security analysis report using all information from previous steps. Format:\n\n**Result:** [Vulnerable/Not Vulnerable/Already Patched] - one line summary\n\n**Impact:** CVSS score and worst-case scenario\n\n**Evidence:** Call chain showing reachability (or why not reachable)\n\n**Fix:** What was changed (if applicable)\n\n**Verification:** Test results (if applicable)\n\nPut the outcome first. Be concise.",
                        },
                    },
                }),
            ],
        },
    "add-and-maintain-readmes-and-backstage-yaml": {
        id: "add-and-maintain-readmes-and-backstage-yaml",
                title: " Backstage catalog standardization",
                description:
                    "Fetches a Backstage RFC from an external source of truth, analyzes the repository it's launched on, and creates or updates the catalog-info.yaml file to meet the RFC, it validates using the Entity using Backstage Validator CLI, creates a pull request, and adds a comment for what requires manual human input.",
                enabled: true,
                icon: IconFileText,
                iconColor: "text-content-brand-accent-02",
                iconBgColor: "bg-surface-brand-accent-02",
                trigger: new WorkflowTrigger({
                    trigger: {
                        case: "manual",
                        value: new WorkflowTrigger_Manual(),
                    },
                    context: new WorkflowTriggerContext({
                        context: {
                            case: "projects",
                            value: new WorkflowTriggerContext_Projects({
                                projectIds: [],
                            }),
                        },
                    }),
                }),
                steps: [
                    new WorkflowStep({
                        step: {
                            case: "agent",
                            value: {
                                prompt: `Fetch and analyze the Backstage catalog standardization RFC from this Gist: https://gist.github.com/loujaybee/7f8ed30f4dd2dee94a5db019b47cce62

Extract and summarize:

All required fields and their specifications
Acceptable values and formats for each field
Fields that can typically be inferred automatically vs. those requiring human input
Validation rules and compliance requirements

Output a structured summary of the RFC requirements that will guide the subsequent steps.`,
                            },
                        },
                    }),
                    new WorkflowStep({
                        step: {
                            case: "agent",
                            value: {
                                prompt: `Examine this repository to understand its characteristics:

What type of component is this? (service, library, website, or documentation)
What is the primary programming language and technology stack?
What frameworks, databases, or notable dependencies are in use?
Does a catalog-info.yaml file already exist at the repository root? If so, what does it contain?
Based on the README, repository structure, and code, what is the purpose of this component?
Is this component actively maintained? (check recent commit history)
Are there any existing configuration files that provide context (package.json, requirements.txt, etc.)?

Provide a comprehensive analysis that will inform the catalog-info.yaml generation.`,
                            },
                        },
                    }),
                    new WorkflowStep({
                        step: {
                            case: "agent",
                            value: {
                                prompt: `Based on the RFC requirements and repository analysis, generate or update the catalog-info.yaml file:

Create the file with proper Backstage catalog format (apiVersion: backstage.io/v1alpha1, kind: Component)
Populate all fields that can be confidently determined automatically:

metadata.name (derived from repository name)
metadata.description (based on repository purpose)
metadata.tags (technology stack and component category)
spec.type (service/library/website/documentation)
spec.lifecycle (production/experimental/deprecated based on activity and context)


For fields requiring human input (spec.owner, spec.system), add TODO comments with clear guidance on what needs to be provided
Include inline YAML comments explaining automated choices where helpful
Ensure valid YAML syntax

If catalog-info.yaml already exists, preserve any manually-added fields and only update missing or outdated automated fields.`,
                            },
                        },
                    }),
                    new WorkflowStep({
                        step: {
                            case: "agent",
                            value: {
                                prompt: `Install the Backstage entity validator and validate the generated catalog-info.yaml file:

npm install --global @roadiehq/backstage-entity-validator

validate-entity catalog-info.yaml

Review the validation output. If there are any errors, fix them and re-validate until the file passes validation.

Report the validation results.`,
                            },
                        },
                    }),
                    new WorkflowStep({
                        step: {
                            case: "pullRequest",
                            value: new WorkflowStep_PullRequest({
                                title: "chore: add Backstage catalog metadata per RFC-2024-023",
                                description: `This PR implements the Backstage catalog standardization requirements defined in RFC-2024-023.

Changes Made:

[Created/Updated] catalog-info.yaml with automated fields
All automated fields populated based on repository analysis
File validated using @roadiehq/backstage-entity-validator

What Was Automated:

Component type, description, and technology tags
Lifecycle determination based on repository activity
Basic metadata derived from repository structure

Requires Manual Review:

spec.owner: Please specify the team responsible for this component
spec.system: Please specify which system/product this component belongs to
Review all automated fields for accuracy

Validation: ✅ Passed Backstage entity validation
Please review the automated fields and complete the manual items before merging.`,
                                branch: "ona/backstage-catalog-standardization",
                                draft: true,
                            }),
                        },
                    }),
                    new WorkflowStep({
                        step: {
                            case: "agent",
                            value: {
                                prompt: `Add a comment to the pull request highlighting what requires human attention:

⚠️ Manual Action Required
While most of the catalog metadata has been populated automatically, the following fields require your input:
Required Manual Fields:

spec.owner (Line XX)

Current: TODO: Specify team
Action needed: Replace with your team identifier (e.g., team:platform-engineering)
See team registry for valid team names

spec.system (Line XX)

Current: TODO: Specify system
Action needed: Specify which product/system this component belongs to
See system definitions or contact Platform Engineering to create a new system

Please Review:

Verify the component description accurately reflects the purpose
Confirm the technology tags are complete
Ensure the lifecycle state (production/experimental/deprecated) is correct

Once you've completed these items, this component will be fully compliant with RFC-2024-023.`,
                    },
                },
            }),
        ],
    },
} as const;

export type AutomationTemplateId = keyof typeof AUTOMATION_TEMPLATES_INTERNAL;

export type AutomationTemplate = {
    id: AutomationTemplateId;
    title: string;
    description: string;
    icon: React.FC<CentralIconBaseProps>;
    iconColor: string;
    iconBgColor: string;
    steps: readonly WorkflowStep[];
    trigger: WorkflowTrigger;
    enabled: boolean;
};

export const AUTOMATION_TEMPLATES: Record<AutomationTemplateId, AutomationTemplate> = AUTOMATION_TEMPLATES_INTERNAL;

export type TemplateGroup = {
    id: string;
    label?: string;
    templates: Record<string, AutomationTemplate>;
};

export const TEMPLATE_GROUPS = {
    "from-scratch": {
        id: "from-scratch",
        label: undefined,
        templates: {
            "start-from-scratch": AUTOMATION_TEMPLATES["start-from-scratch"],
        },
    },
    "launch-use-cases": {
        id: "launch-use-cases",
        label: "Common use-cases",
        templates: {
            "automated-dev-environment-setup": AUTOMATION_TEMPLATES["automated-dev-environment-setup"],
            "cve-mitigation-and-version-updates": AUTOMATION_TEMPLATES["cve-mitigation-and-version-updates"],
            "add-and-maintain-readmes-and-backstage-yaml": AUTOMATION_TEMPLATES["add-and-maintain-readmes-and-backstage-yaml"],
        },
    },
};

export type TemplateGroupId = keyof typeof TEMPLATE_GROUPS;

export const getTemplateById = (id: string): AutomationTemplate | undefined =>
    AUTOMATION_TEMPLATES[id as AutomationTemplateId];

export const newWorkflowFromTemplate = (template: AutomationTemplate) => {
    return new Workflow({
        metadata: {
            name: template.title,
            description: template.description,
        },
        spec: {
            triggers: [template.trigger],
            action: new WorkflowAction({
                limits: new WorkflowAction_Limits({
                    maxParallel: DEFAULT_WORKFLOW_LIMITS.maxParallel,
                    maxTotal: DEFAULT_WORKFLOW_LIMITS.maxTotal,
                }),
                steps: [...template.steps],
            }),
        },
    });
};
